import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import { EmployeesEmployeeSelectorInput } from 'generated/resources/datatable.graphql'
import { ExportTrainingsTeamFragment, FullTrainingFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useTrainingsCategories } from 'modules/trainings/hooks/Categories/useTrainingsCategories'
import { useTrainingsExport } from 'modules/trainings/hooks/Exports/useTrainingsExport'
import { useTrainingsSettings } from 'modules/trainings/hooks/Settings/useTrainingsSettings'

type ExportType = 'training' | 'employee' | 'relatorio'
type Format = 'excel' | 'csv' | 'xml'

type FormatsRecord = Record<string, Format>
const FORMATS: FormatsRecord = {
  excel: 'excel',
  csv: 'csv',
  xml: 'xml',
}

type ExportsRecord = Record<string, ExportType>
const EXPORTS: ExportsRecord = {
  employee: 'employee',
  training: 'training',
  relatorio: 'relatorio',
}

interface ExportTrainingsFormValues {
  ids: string[]
  teamIds: string[]
  employeeSelection: EmployeesEmployeeSelectorInput
  categoryIds: string[]
  exportType: string
  format: string
  filename: string
  status: string[]
  from: string
  to: string
  scheduleAccessId: string
  companyId: string
  legalEntityId: string
  cae?: string
  idEntidade?: string
  referenceYear?: string
}

export const ExportTrainings = ({
  teams,
  loading,
  trainings,
}: {
  teams: ExportTrainingsTeamFragment[]
  trainings: FullTrainingFragment[]
  loading: boolean
}) => {
  const now = moment()
  const startDate = now.startOf('year').format(BACKEND_DATE_FORMAT)
  const endDate = now.endOf('year').format(BACKEND_DATE_FORMAT)
  const { categories } = useTrainingsCategories()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const navigateToTrainings = () => {
    navigate(navigation.trainings.list.index.fullPath())
  }
  const { exportTrainings } = useTrainingsExport()
  const { company } = useCurrentSession()
  const { settings } = useTrainingsSettings(company.id)
  const setting = settings?.at(0)
  const relatorioEnabled = !!setting?.relatorioEnabled
  const legalEntities = company.legalEntities
  const mainLegalEntityId = legalEntities.find((l) => l.main)?.id

  const {
    access: { id: userId },
  } = useCurrentSession()
  const [selected, setSelectedEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })
  const form = useForm<ExportTrainingsFormValues>({
    defaultValues: {
      filename: i18n.t('trainings.export_trainings.fields.default_name', {
        date: moment().format('YYYY/MM/DD'),
      }),
      from: startDate,
      to: endDate,
      ids: [],
      teamIds: [],
      employeeSelection: {
        type: 'manual',
        ids: [],
      },
      categoryIds: [],
      scheduleAccessId: userId,
      companyId: company.id,
      exportType: 'employee',
      format: 'excel',
      status: [],
      legalEntityId: String(mainLegalEntityId),
    },
    validate: (data) => {
      const errors: FormErrors<ExportTrainingsFormValues> = {}

      if (!data.filename || data.filename === '') {
        errors.filename = i18n.t('common.empty')
      }

      if (data.exportType === EXPORTS.relatorio && data.format === FORMATS.xml) {
        if (!data.cae) {
          errors.cae = i18n.t('common.empty')
        } else {
          // Validate CAE format: must be a 5-digit number between 01111 and 99000
          const caeNumber = parseInt(data.cae, 10)
          if (isNaN(caeNumber) || caeNumber < 1111 || caeNumber > 99000) {
            errors.cae = i18n.t('trainings.export_trainings.fields.cae_error')
          }
        }

        if (!data.referenceYear) {
          errors.referenceYear = i18n.t('trainings.export_trainings.fields.reference_year_error')
        } else {
          // Validate year format: must be a 4-digit year (e.g., 2024)
          const yearRegex = /^\d{4}$/
          if (!yearRegex.test(data.referenceYear)) {
            errors.referenceYear = i18n.t(
              'trainings.export_trainings.fields.reference_year_format_error'
            )
          } else {
            // Additional validation: year should be reasonable (between 1900 and current year + 10)
            const year = parseInt(data.referenceYear, 10)
            const currentYear = new Date().getFullYear()
            if (year < 1900 || year > currentYear + 10) {
              errors.referenceYear = i18n.t(
                'trainings.export_trainings.fields.reference_year_range_error'
              )
            }
          }
        }

        if (!data.idEntidade) {
          errors.idEntidade = i18n.t('trainings.export_trainings.fields.id_entidade_error')
        }
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await exportTrainings({
        ...data,
        employeeSelection: selected,
        exportType: data.exportType,
        from: data.from,
        to: data.to,
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.export_trainings.error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.export_trainings.success'),
        })

        navigateToTrainings()
      }

      return handled
    },
  })
  return (
    <Modal
      title={i18n.t('trainings.export_trainings.title')}
      description={i18n.t('trainings.export_trainings.description')}
      onClose={navigateToTrainings}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button.Router to={navigation.trainings.list.index.fullPath()} type="secondary">
            {i18n.t('trainings.export_trainings.cancel')}
          </Button.Router>
          <Button submit>{i18n.t('trainings.export_trainings.export')}</Button>
        </Box>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Box gap="s16">
          <TextField
            defaultValue={form.values.filename}
            label={i18n.t('trainings.export_trainings.fields.name')}
            onChange={(value) => form.setValues({ ...form.values, filename: value })}
          />
          <Select
            value={form.values.exportType}
            label={i18n.t('trainings.export_trainings.fields.type')}
            options={[
              {
                label: i18n.t('trainings.export_trainings.fields.types.employee_summary'),
                value: EXPORTS.employee,
              },
              {
                label: i18n.t('trainings.export_trainings.fields.types.training_summary'),
                value: EXPORTS.training,
              },
              ...(relatorioEnabled
                ? [
                    {
                      label: i18n.t('trainings.export_trainings.fields.types.relatorio'),
                      value: EXPORTS.relatorio,
                    },
                  ]
                : []),
            ]}
            onChange={(value) => {
              const newValues = { ...form.values, exportType: value }
              if (value === EXPORTS.relatorio && form.values.format === FORMATS.csv) {
                newValues.format = 'excel'
              }
              form.setValues(newValues)
            }}
          />
          <Select
            value={form.values.format}
            label={i18n.t('trainings.export_trainings.fields.format')}
            options={[
              {
                label: i18n.t('trainings.export_trainings.fields.formats.excel'),
                value: FORMATS.excel,
              },
              ...(form.values.exportType === EXPORTS.relatorio
                ? [
                    {
                      label: i18n.t('trainings.export_trainings.fields.formats.xml'),
                      value: FORMATS.xml,
                    },
                  ]
                : []),
              ...(form.values.exportType !== EXPORTS.relatorio
                ? [
                    {
                      label: i18n.t('trainings.export_trainings.fields.formats.csv'),
                      value: FORMATS.csv,
                    },
                  ]
                : []),
            ]}
            onChange={(value) => form.setValues({ ...form.values, format: value })}
          />
          {trainings && (
            <Selector.Popover
              label={i18n.t('trainings.export_trainings.fields.training')}
              placeholder={i18n.t('trainings.export_trainings.fields.trainings.placeholder')}
              searchPlaceholder={i18n.t('common.search')}
              blankLabel=""
              value={form.values.ids}
              onChange={(selection) =>
                form.setValues({ ...form.values, ids: Array.isArray(selection) ? selection : [] })
              }
              isMultiSelect
            >
              {trainings.map((training) => (
                <Selector.Option
                  key={training.name}
                  value={training.id}
                  label={training.name}
                  searchValue={training.name}
                >
                  {training.name}
                </Selector.Option>
              ))}
            </Selector.Popover>
          )}
          {form.values.exportType === EXPORTS.training && (
            <>
              <Selector.Popover
                label={i18n.t('trainings.export_trainings.fields.team')}
                placeholder={i18n.t('trainings.export_trainings.fields.teams.placeholder')}
                searchPlaceholder={i18n.t('common.search')}
                blankLabel=""
                value={form.values.teamIds}
                onChange={(selection) =>
                  form.setValues({
                    ...form.values,
                    teamIds: Array.isArray(selection) ? selection : [],
                  })
                }
                isMultiSelect
              >
                {teams.map((team) => (
                  <Selector.Option
                    key={team.name}
                    value={team.id}
                    label={team.name}
                    searchValue={team.name}
                  >
                    {team.name}
                  </Selector.Option>
                ))}
              </Selector.Popover>
              {categories && (
                <Selector.Popover
                  label={i18n.t('trainings.export_trainings.fields.categories')}
                  placeholder={i18n.t(
                    'trainings.export_trainings.fields.categories_selector.placeholder'
                  )}
                  searchPlaceholder={i18n.t('common.search')}
                  blankLabel=""
                  value={form.values.categoryIds}
                  onChange={(selection) =>
                    form.setValues({
                      ...form.values,
                      categoryIds: Array.isArray(selection) ? selection : [],
                    })
                  }
                  isMultiSelect
                >
                  {categories.map((category) => (
                    <Selector.Option
                      key={category.name}
                      value={category.id}
                      label={category.name}
                      searchValue={category.name}
                    >
                      {category.name}
                    </Selector.Option>
                  ))}
                </Selector.Popover>
              )}
            </>
          )}
          {form.values.exportType === EXPORTS.training ||
            (form.values.exportType === EXPORTS.relatorio && (
              <Selector.Popover
                label={i18n.t('trainings.export_trainings.fields.status')}
                placeholder={i18n.t('trainings.export_trainings.fields.statuses.placeholder')}
                searchPlaceholder={i18n.t('common.search')}
                blankLabel=""
                value={form.values.status}
                onChange={(selection) =>
                  form.setValues({
                    ...form.values,
                    status: Array.isArray(selection) ? selection : [],
                  })
                }
                isMultiSelect
              >
                <Selector.Option
                  key={'draft'}
                  value={'draft'}
                  label={i18n.t('trainings.trainings_table.status.draft')}
                  searchValue={'draft'}
                >
                  {i18n.t('trainings.trainings_table.status.draft')}
                </Selector.Option>
                <Selector.Option
                  key={'active'}
                  value={'active'}
                  label={i18n.t('trainings.trainings_table.status.active')}
                  searchValue={'active'}
                >
                  {i18n.t('trainings.trainings_table.status.active')}
                </Selector.Option>
              </Selector.Popover>
            ))}
          {form.values.exportType === EXPORTS.employee && (
            <EmployeeSelectorV2 multiple onChange={setSelectedEmployees} />
          )}
          {form.values.exportType === EXPORTS.relatorio && (
            <Box gap="s6">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.export_trainings.fields.legal_entities')}
              </Text.Normal>
              <Selector.Popover
                placeholder={i18n.t('trainings.training_relatorio_fields.option_placeholder_minus')}
                headerLabel={i18n.t('trainings.training_relatorio_fields.area_of_education')}
                searchPlaceholder={i18n.t('trainings.training_relatorio_fields.search')}
                isMultiSelect={false}
                blankLabel={i18n.t('trainings.training_relatorio_fields.none')}
                value={[form.values.legalEntityId]}
                onChange={(selection) =>
                  form.setValues({
                    ...form.values,
                    legalEntityId: Array.isArray(selection) ? selection[0] : selection,
                  })
                }
                mandatory
              >
                {legalEntities.map((legalEntity) => (
                  <Selector.Option
                    key={legalEntity.id}
                    value={legalEntity.id}
                    label={legalEntity.legalName}
                    searchValue={legalEntity.legalName}
                  >
                    {legalEntity.legalName}
                  </Selector.Option>
                ))}
              </Selector.Popover>
              {form.values.format === FORMATS.xml && (
                <TextField
                  label={i18n.t('trainings.export_trainings.cae')}
                  value={form.values.cae}
                  onChange={(value) => form.setValues({ ...form.values, cae: value })}
                  error={form.errors.cae}
                />
              )}
              {form.values.format === FORMATS.xml && (
                <TextField
                  label={i18n.t('trainings.export_trainings.reference_year')}
                  value={form.values.referenceYear}
                  onChange={(value) => {
                    // Only allow numeric characters and limit to 4 digits
                    const numericValue = value.replace(/\D/g, '').slice(0, 4)
                    form.setValues({ ...form.values, referenceYear: numericValue })
                  }}
                  error={form.errors.referenceYear}
                />
              )}
              {form.values.format === FORMATS.xml && (
                <TextField
                  label={i18n.t('trainings.export_trainings.id_entidade')}
                  value={form.values.idEntidade}
                  onChange={(value) => form.setValues({ ...form.values, idEntidade: value })}
                  error={form.errors.idEntidade}
                />
              )}
            </Box>
          )}
          <Box flexDirection="row" gap="s16">
            <DayPicker
              mode="single"
              label={i18n.t('trainings.export_trainings.fields.from')}
              onChange={(date) => {
                form.setValues({
                  ...form.values,
                  from: dateUIToBackend(date),
                })
              }}
              value={dateBackendToUI(form.values.from)}
            />
            <DayPicker
              mode="single"
              label={i18n.t('trainings.export_trainings.fields.to')}
              onChange={(date) => form.setValues({ ...form.values, to: dateUIToBackend(date) })}
              value={dateBackendToUI(form.values.to)}
            />
          </Box>
        </Box>
      )}
    </Modal>
  )
}
